/**
 * Utility functions for formatting data in the application
 */

/**
 * Format a number as currency
 * @param {number|string} value - The value to format
 * @param {string} locale - The locale to use for formatting (default: 'fr-DZ')
 * @param {string} currency - The currency code (default: 'DZD')
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (value, locale = 'fr-DZ', currency = 'DZD') => {
  if (value === null || value === undefined) return '-';
  
  // Convert string to number if needed
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  
  // Check if it's a valid number
  if (isNaN(numValue)) return '-';
  
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(numValue);
  } catch (error) {
    console.error('Error formatting currency:', error);
    return `${numValue} ${currency}`;
  }
};

/**
 * Format a date string to a localized date format
 * @param {string} dateString - ISO date string
 * @param {string} locale - The locale to use (default: 'fr-DZ')
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString, locale = 'fr-DZ') => {
  if (!dateString) return '-';
  
  try {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(date);
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString;
  }
};

/**
 * Format a time duration in minutes to a human-readable format
 * @param {number} minutes - Duration in minutes
 * @returns {string} Formatted duration (e.g., "1h 30min")
 */
export const formatDuration = (minutes) => {
  if (minutes === null || minutes === undefined) return '-';
  
  const numMinutes = typeof minutes === 'string' ? parseInt(minutes, 10) : minutes;
  
  if (isNaN(numMinutes)) return '-';
  
  const hours = Math.floor(numMinutes / 60);
  const mins = numMinutes % 60;
  
  if (hours > 0) {
    return `${hours}h${mins > 0 ? ` ${mins}min` : ''}`;
  }
  
  return `${mins}min`;
};

/**
 * Parse a duration string in format "1h30min" or "45min" or "2h" to minutes
 * @param {string} durationStr - Duration string to parse
 * @returns {number} Total minutes
 */
export const parseDurationString = (durationStr) => {
  if (!durationStr) return 0;
  
  // Handle different formats
  let hours = 0;
  let minutes = 0;
  
  // Extract hours if present (e.g., "2h30min" or "2h")
  const hoursMatch = durationStr.match(/([0-9]+)\s*h/i);
  if (hoursMatch && hoursMatch[1]) {
    hours = parseInt(hoursMatch[1], 10);
  }
  
  // Extract minutes if present (e.g., "2h30min" or "30min")
  const minutesMatch = durationStr.match(/([0-9]+)\s*min/i);
  if (minutesMatch && minutesMatch[1]) {
    minutes = parseInt(minutesMatch[1], 10);
  }
  
  // If no pattern matched but it's just a number, assume it's minutes
  if (hours === 0 && minutes === 0 && /^[0-9]+$/.test(durationStr.trim())) {
    minutes = parseInt(durationStr.trim(), 10);
  }
  
  return (hours * 60) + minutes;
};
